import { useEffect, useState } from 'react';
import Button from './Button';
import GetDidCta from './GetDidCta';
import { OFFICE_COUNTRIES } from '../data/officeCountries';
import { persistAndSubmitApplication } from '../web3/persistApplication';
import { ensureSessionProof, notifyApplicationSubmitted } from '../web3/serverApi';
import type { SessionProof } from '../web3/serverContract';
import {
  applicationSubmitError,
  submitErrorNeedsDidOnboarding,
} from '../web3/didEligibility';
import { sealApplicationForInsert } from '../web3/sealApplication';
import { useWeb3Auth } from '../web3/Web3AuthContext';
import { buildOfficerReapplyState } from '../web3/officerDirectory';
import {
  getRegistryUser,
  listRegistryApplications,
  statusLabel,
} from '../web3/registryClient';

type FormData = {
  fullName: string;
  dao1ReferralLink: string;
  email: string;
  country: string;
};

const SportsOfficerForm = () => {
  const web3 = useWeb3Auth();
  const walletDid = web3.eligibility?.didId ?? web3.claim?.didId ?? null;
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    dao1ReferralLink: '',
    email: '',
    country: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reapply, setReapply] = useState<ReturnType<typeof buildOfficerReapplyState> | null>(
    null
  );

  useEffect(() => {
    const wallet = web3.address;
    const chainId = web3.chainId;
    if (!wallet || !chainId) {
      setReapply(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [user, apps] = await Promise.all([
          getRegistryUser(chainId, wallet),
          listRegistryApplications(chainId),
        ]);
        if (cancelled) return;
        setReapply(
          buildOfficerReapplyState({
            wallet,
            liveDid: walletDid,
            registeredDid: user.registeredAt ? user.didId : null,
            apps,
          })
        );
      } catch {
        if (!cancelled) setReapply(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [web3.address, web3.chainId, walletDid]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.fullName) newErrors.fullName = 'Full name is required';
    if (!formData.dao1ReferralLink) newErrors.dao1ReferralLink = 'DAO1 Referral Link is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!walletDid) newErrors.didNumber = 'Connect a wallet that holds a Global DID to apply as an officer';
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validate DAO1 referral link format
    if (formData.dao1ReferralLink && !formData.dao1ReferralLink.match(/^https:\/\/app\.dao1\.ai\/auth\/enroll\?referral=\d+$/)) {
      newErrors.dao1ReferralLink = 'Please enter a valid DAO1 referral link';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const sendTelegramNotification = async (didId: number, session: SessionProof) => {
    try {
      const { sent } = await notifyApplicationSubmitted({ role: 'officer', didId, session });
      if (!sent) console.error('Telegram bot configuration missing');
    } catch (error) {
      console.error('Failed to send Telegram notification:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setSubmitting(true);
      try {
        const sealed = await sealApplicationForInsert({
          role: 'officer',
          connectedAddress: web3.address,
          plaintext: {
            full_name: formData.fullName,
            dao1_referral_link: formData.dao1ReferralLink,
            email: formData.email || null,
            country: formData.country,
          },
        });

        const raw = web3.getRawProvider();
        if (!raw || !web3.chainId) {
          throw new Error('Connect your wallet on Apertum to submit on-chain.');
        }
        const session = await ensureSessionProof(web3.signIn);
        await persistAndSubmitApplication({
          sealed,
          role: 'officer',
          session,
          rawProvider: raw,
          chainId: web3.chainId,
          publicMeta: {
            country: formData.country,
            replacesApplicationId: reapply?.latest?.id ?? null,
          },
        });

        await sendTelegramNotification(sealed.did_id, session);
        setSubmitted(true);
      } catch (error) {
        console.error('Error submitting form:', error);
        setErrors(prev => ({
          ...prev,
          submit: applicationSubmitError(error),
        }));
      } finally {
        setSubmitting(false);
      }
    }
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
        <h3 className="text-2xl font-semibold text-green-800 mb-4">
          Thank you for your application!
        </h3>
        <p className="text-green-700 mb-6">
          {reapply?.didChanged
            ? `Reapplication received for DID #${walletDid}. A reviewer will replace the previous officer record after approval.`
            : 'We will review your application and get back to you soon.'}
        </p>
        <Button
          variant="secondary"
          onClick={() => setSubmitted(false)}
        >
          Submit Another Application
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {reapply?.isReapply && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold mb-1">
            {reapply.didChanged ? 'Reapply with your new DID' : 'You already have an officer application'}
          </p>
          <p>
            {reapply.didChanged
              ? `This wallet was bound to DID #${reapply.previousDid}. Submitting will refresh the on-chain binding to DID #${walletDid} and open a new pending review.`
              : `Latest application #${reapply.latest?.id ?? '—'} is ${
                  reapply.latest ? statusLabel(reapply.latest.status) : 'on file'
                }. You can submit again to update country or contact details.`}
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.fullName ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.fullName && <p className="mt-1 text-sm text-red-500">{errors.fullName}</p>}
        </div>

        <div>
          <label htmlFor="dao1ReferralLink" className="block text-sm font-medium text-gray-700 mb-1">
            DAO1 Referral Link <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            id="dao1ReferralLink"
            name="dao1ReferralLink"
            value={formData.dao1ReferralLink}
            onChange={handleChange}
            placeholder="https://app.dao1.ai/auth/enroll?referral=XXXXX"
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.dao1ReferralLink ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.dao1ReferralLink && <p className="mt-1 text-sm text-red-500">{errors.dao1ReferralLink}</p>}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address (Optional)
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
            Country <span className="text-red-500">*</span>
          </label>
          <select
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.country ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select country</option>
            {OFFICE_COUNTRIES.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
          {errors.country && <p className="mt-1 text-sm text-red-500">{errors.country}</p>}
        </div>

        <div>
          <label htmlFor="didNumber" className="block text-sm font-medium text-gray-700 mb-1">
            DID Number
          </label>
          <input
            type="text"
            id="didNumber"
            name="didNumber"
            value={walletDid != null ? String(walletDid) : ''}
            disabled
            className="w-full px-4 py-2 border rounded-md bg-gray-100 text-gray-600"
          />
          <p className="mt-1 text-xs text-gray-500">
            Read from the connected wallet when you submit. Connecting alone does not record it.
          </p>
          {errors.didNumber && <p className="mt-1 text-sm text-red-500">{errors.didNumber}</p>}
          {!walletDid && (
            <div className="mt-3">
              <GetDidCta
                compact
                reason={web3.eligibility?.reason ?? 'missing_did'}
                message="This wallet has no Global DID yet. Get one at kyc.apertum.io, then return here to apply."
              />
            </div>
          )}
        </div>
      </div>

      {errors.submit && (
        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {errors.submit}
          </div>
          {submitErrorNeedsDidOnboarding(errors.submit) && (
            <GetDidCta compact reason="missing_did" message={errors.submit} />
          )}
        </div>
      )}

      <div className="pt-4">
        <Button 
          type="submit" 
          variant="primary" 
          size="lg" 
          className="w-full"
          disabled={submitting}
        >
          {submitting
            ? 'Submitting...'
            : reapply?.didChanged
              ? 'Reapply with new DID'
              : reapply?.isReapply
                ? 'Submit updated application'
                : 'Submit Application'}
        </Button>
      </div>
    </form>
  );
};

export default SportsOfficerForm;